import BigNumber from 'bignumber.js';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { erc20ABI } from 'wagmi';
import * as Viem from 'viem';

import { prepareErrorMessage } from '#/features/wallet/ethereum';
import { logException } from '#/features/logging/logging';
import { useWalletState } from '#/features/wallet/wallet-context';
import {
  DepositState,
  useDepositState,
} from '#/features/deposit/deposit-context';
import { BridgedToken } from '#/features/paraclear';
import EthereumWallet, {
  EthereumAddress,
} from '#/features/ethereum-wallet/wallet';

import { AsyncResult } from '#/utils/types';

const USDC_INCREASE_ALLOWANCE_AMOUNT = '100000';

interface IncreaseEthereumBridgeAllowance {
  (token: BridgedToken): AsyncResult<void>;
}

export default function useIncreaseEthereumBridgeAllowance(): IncreaseEthereumBridgeAllowance {
  const [walletState] = useWalletState();
  const [depositState, setDepositState] = useDepositState();

  return useCallback(
    async function increaseEthereumBridgeAllowance(token: BridgedToken) {
      if (walletState.ethereumAddress == null) {
        throw new Error(
          'increaseEthereumBridgeAllowance called before ethereumAddress available',
        );
      }

      if (depositState.status !== 'idle') {
        throw new Error(
          `Deposit is already in progress (status is ${depositState.status}).`,
        );
      }

      const actions = prepareActions(setDepositState);

      actions.resetError();

      try {
        actions.setStatus('busy');
        await requestAllowanceIncrease(walletState.ethereumAddress, token);
      } catch (err) {
        const description = 'Failed to increase Ethereum bridge allowance';
        const { message, isException } = prepareErrorMessage(description, err);
        actions.setError(message);
        actions.setStatus('idle');
        if (isException) {
          const error = new Error(description, { cause: err });
          logException(error);
          return { ok: false, error, reason: description };
        }
        return { ok: false, error: null, reason: description };
      }
      const increaseAllowanceValue = BigNumber(
        Viem.parseUnits(
          USDC_INCREASE_ALLOWANCE_AMOUNT,
          token.decimals,
        ).toString(),
      );
      actions.updateAllowance(increaseAllowanceValue);
      actions.setStatus('idle');
      return { ok: true, data: undefined };
    },
    [depositState.status, setDepositState, walletState.ethereumAddress],
  );
}

async function requestAllowanceIncrease(
  ethereumAddress: EthereumAddress,
  token: BridgedToken,
): Promise<{ hash: string }> {
  const spender = token.l1BridgeAddress;
  const value = Viem.parseUnits(
    USDC_INCREASE_ALLOWANCE_AMOUNT,
    token.decimals,
  ).toString();

  const { request } = await EthereumWallet.prepareWriteContract({
    address: token.l1TokenAddress,
    abi: erc20ABI,
    functionName: 'approve',
    args: [spender, BigInt(value)],
    /* the account to send the transaction from. */
    account: ethereumAddress,
  });

  const { hash } = await EthereumWallet.writeContract(request);
  // WIP: setup global default timeout for `waitForTransaction` inside EthereumWallet implementation?
  await EthereumWallet.waitForTransaction({
    hash,
    timeout: 120_000, // 2 minutes
  });

  return { hash };
}

function prepareActions(setState: Dispatch<SetStateAction<DepositState>>) {
  function setStatus(status: DepositState['status']) {
    setState((state) => ({ ...state, status }));
  }

  function updateAllowance(allowance: DepositState['allowance']) {
    setState((state) => ({ ...state, allowance }));
  }

  const setError = (error: string) => {
    setState((state) => ({ ...state, error }));
  };

  const resetError = () => {
    setState((state) => ({ ...state, error: '' }));
  };

  return { setStatus, updateAllowance, setError, resetError };
}
